import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models, Embed, service } from 'powerbi-client';
import { account, useMsal } from '../utils/authContext';
import { apiRequest } from '../utils/authConfig';
import { getHeaders } from '../utils';
import { RenderErrorMessage } from '../app/App';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'react-bootstrap';

const api = process.env['REACT_APP_BACKEND_URL'];
const apiKey = process.env.REACT_APP_BACKEND_KEY ? process.env.REACT_APP_BACKEND_KEY : '';

export type ReportParams = {
    eventId: string;
  };

const Reporting = () => {
    const { getToken } = useMsal();
    const location = useLocation();
    let eventId  = (location.state as any).eventId;
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState<string>();
    const [report, setReport] = useState<Embed>();
    const navigate = useNavigate();
    const [token, setToken]: [token: { accessToken: string, idToken: string } | undefined, setToken: Function] = useState();
    const [embedConfig, setEmbedConfig]: [models.IReportEmbedConfiguration, Function] = useState({
        type: 'report',   // Supported types: report, dashboard, tile, visual and qna
        id: undefined,
        embedUrl: undefined,
        accessToken: undefined,
        viewMode: models.ViewMode.View,
        tokenType: models.TokenType.Embed,
        permissions: models.Permissions.Read,
        settings: undefined,
    });
    const fullscreen = () => {
       report?.fullscreen();
    }
    const printReport = () => {
       (report as any)?.print();
    }
    const args = useMemo(() => ({ ...apiRequest, account: account() }), []);
    const getUserTokenAsync = useCallback(async () => {
        if (!loading && !token?.accessToken) {
            setLoading(true);
            try {
                const { accessToken, idToken } = await getToken(args);
                setToken({ accessToken, idToken });
            } catch (err: any) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
    }, [getToken, args, loading, token?.accessToken]);
    useEffect(() => {
        getUserTokenAsync();
    }, [args, getUserTokenAsync]);

    const getReportConfig = useCallback(() => {
        if (!loading && token?.accessToken && !loaded && !error) {
            console.log('loading BIEmbed credentials ...');
            setLoading(true);
            (async () => {
                const headers = getHeaders(apiKey, token);
                const body = {
                    workspaceId: '3df8f56e-dc26-4bcb-bfdd-b11929472516',
                    reportId: '5d87816f-b469-426e-a64f-d00a6d68e6f8',
                };
                try {
                    const res = await fetch(`${api}/report/request/biembed`, {
                        method: 'Post',
                        headers,
                        body: JSON.stringify(body),
                    });
                    if (!res.ok) throw new Error("Unable to retrieve repot.");
                    const json = await res.json();
                    if (json.error) throw new Error(json.error);
                    const embedObj = json.embedUrl[0] || {};
                    const eventLookUpNameFilter: models.IBasicFilter = {
                        $schema: "http://powerbi.com/product/schema#basic",
                        target: {
                            table: "Events",
                            column: "Event Look Up Name"
                        },
                        operator: "In",
                        values: [eventId],
                        filterType: models.FilterType.Basic
                    };
                    const config: models.IReportEmbedConfiguration = {
                        ...embedConfig,
                        embedUrl: embedObj.embedUrl,
                        accessToken: json.accessToken,
                        id: embedObj.reportId,
                        permissions: models.Permissions.Read,
                        settings: {
                            localeSettings: {
                                language: "en",
                                formatLocale: "es"
                            },
                            panes: {
                                visualizations: {
                                    visible: true,
                                },
                                filters: {
                                    visible: false,
                                },
                                pageNavigation: {
                                    visible: false,
                                }
                            },

                        },
                        filters: [
                            eventLookUpNameFilter,
                            // eventFiscalYearFilter,
                        ]
                    }
                    setLoaded(true);
                    setEmbedConfig(config);

                } catch (err: any) {
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            })();
        }
    }, [embedConfig, loading, token, loaded, error, eventId]);
    useEffect(() => {
        getReportConfig();
    }, [getReportConfig]);
    const eventHandlersMap = new Map([
        ['loaded', function () {
            console.log('Report has loaded');
        }],
        ['rendered', function () {
            console.log('Report has rendered');
        }],
        ['error', function (event?: service.ICustomEvent<any>) {
            if (event) {
                console.error(event.detail);
            }
        }]
    ]);
    return React.createElement(
        'div', null,
        <>
            {error && <RenderErrorMessage errorMessage={error} />}
            <Button className="ms-2" variant="light" size="sm" onClick={() => navigate(`/event/`, {state: {eventId}})}>
                <FontAwesomeIcon icon="left-long" /> Back to {eventId}
            </Button>
            <Button className="ms-2" size="sm" onClick={() => printReport()}>
                Print <FontAwesomeIcon icon="print" />
            </Button>
            <Button className="ms-2" size="sm" onClick={() => fullscreen()}>
                Fullscreen <FontAwesomeIcon icon="expand" />
            </Button>
            {!error && <PowerBIEmbed
                embedConfig={embedConfig}
                eventHandlers={eventHandlersMap}
                cssClassName={"report-style-class"}
                getEmbeddedComponent={(embedObject: Embed) => {
                    console.log(`Embedded object of type "${embedObject.embedtype}" received`, embedObject);
                    embedObject.iframe.setAttribute('style', 'min-height:130vh;width:100%;');
                    setReport(embedObject);
                }}
            />}
        </>
    );
};

export default Reporting;
